/**
 * DistortionSkillEffect — Specter veil-path active: sighting dismissal field.
 *
 * Toggle a reality-warping effect. While active, each detection tick that
 * would result in a genuine sighting rolls against dismissChance (base 20%).
 * On success, the sighting is silently discarded — no aggro, no escalation.
 * On failure, the sighting resolves normally.
 *
 * The key public method is `attemptDismiss(enemyId)`, called by the AI's
 * detection-tick resolver whenever a genuine sighting would commit. Returns
 * true if dismissed (suppress the sighting), false if the sighting proceeds.
 *
 * Upgrade hooks (pre-wired, values resolved at each call):
 * - Spooked (dismissChance +0.1, noReengageDurationMs +6000): bonus dismiss
 *   chance; on dismiss, ai.setNoReengage(enemyId, noReengageDurationMs) so
 *   the enemy skips future sighting rolls from the same position.
 * - Cascade (cascadeRadius, cascadeChance): on dismiss, scan nearby enemies
 *   via ai.getNearbyEnemies(); each rolls cascadeChance. Non-recursive.
 * - Credence (dismissedDamageBonus, credenceBonusDurationMs): on dismiss,
 *   ai.setCredenceVulnerable(enemyId, credenceBonusDurationMs, damageBonus)
 *   tags the enemy for increased damage for the duration.
 *
 * Extends ToggleSkillEffect. Fizzle on first activation at low XP.
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see specter-spec.md § Distortion, Spooked, Cascade, Credence
 */

import { ToggleSkillEffect } from '../../ToggleSkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface DistortionDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    hasEffect(skillId: string, key: string): boolean;
    getTreePassiveEffect(treeName: string, key: string, aggregation: 'sum' | 'max'): number;
  };
  readonly targeting: {
    shouldFizzle(): boolean;
  };
  readonly rng: {
    /** Returns a value in [0, 1) for dismiss/cascade rolls. */
    roll(): number;
  };
  readonly ai: {
    /** Flag enemy to skip sighting rolls from their current position (Spooked). */
    setNoReengage(enemyId: string, durationMs: number): void;
    /** Return enemy IDs within the given pixel radius of the source enemy (Cascade). */
    getNearbyEnemies(sourceEnemyId: string, radiusPx: number): string[];
    /** Tag the enemy with a damage-bonus window (Credence). */
    setCredenceVulnerable(enemyId: string, durationMs: number, damageBonus: number): void;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface DistortionConfig {
  readonly cooldownMs: number;
  readonly baseDismissChance: number;
  readonly tileSizePx: number;
}

const DEFAULT_CONFIG: DistortionConfig = {
  cooldownMs: 3000,         // long cooldown tier
  baseDismissChance: 0.2,   // 20% base dismiss chance
  tileSizePx: 32,
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class DistortionSkillEffect extends ToggleSkillEffect {
  override readonly skillId: string = 'distortion';

  private readonly deps: DistortionDeps;
  private readonly config: DistortionConfig;

  // Observable state
  private _fizzled = false;
  private _dismissCount = 0;
  private _cascadeDismissCount = 0;

  // Resolved on each attemptDismiss call
  private _resolvedDismissChance = 0;

  constructor(deps: DistortionDeps, config: Partial<DistortionConfig> = {}) {
    super();
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get fizzled(): boolean { return this._fizzled; }
  get dismissCount(): number { return this._dismissCount; }
  get cascadeDismissCount(): number { return this._cascadeDismissCount; }
  get resolvedDismissChance(): number { return this._resolvedDismissChance; }

  // --- ToggleSkillEffect interface ---

  canActivate(): boolean {
    return this.cooldownRemaining <= 0 && !this._isActive;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    this._fizzled = this.deps.targeting.shouldFizzle();
    if (this._fizzled) {
      this.cooldownRemaining = this.config.cooldownMs;
      return false;
    }

    this._isActive = true;
    this._dismissCount = 0;
    this._cascadeDismissCount = 0;
    return true;
  }

  protected onDeactivate(): void {
    this.cooldownRemaining = this.config.cooldownMs;
  }

  protected updateWhileActive(_delta: number): void {
    // No per-frame logic — all dismiss logic driven by attemptDismiss() calls
  }

  // --- Public: called by AI detection resolver ---

  /**
   * Called by the AI when a genuine sighting is about to commit.
   * Returns true if the sighting is dismissed (suppressed), false if it proceeds.
   * No-op (returns false) when Distortion is not active.
   */
  attemptDismiss(enemyId: string): boolean {
    if (!this._isActive) return false;

    this._resolvedDismissChance = this.config.baseDismissChance
      + this.deps.resolver.getAdditiveEffect('distortion', 'dismissChance');

    const roll = this.deps.rng.roll();
    if (roll >= this._resolvedDismissChance) {
      return false; // sighting proceeds normally
    }

    // Sighting dismissed
    this._onDismiss(enemyId, false);
    return true;
  }

  // --- Private ---

  private _onDismiss(enemyId: string, isCascade: boolean): void {
    if (isCascade) {
      this._cascadeDismissCount++;
    } else {
      this._dismissCount++;
    }

    // Spooked: no-reengage window at the dismiss position
    const noReengageMs = this.deps.resolver.getAdditiveEffect('distortion', 'noReengageDurationMs');
    if (noReengageMs > 0) {
      this.deps.ai.setNoReengage(enemyId, noReengageMs);
    }

    // Credence: damage bonus window
    const credenceDurationMs = this.deps.resolver.getAdditiveEffect('distortion', 'credenceBonusDurationMs');
    if (credenceDurationMs > 0) {
      const damageBonus = this.deps.resolver.getAdditiveEffect('distortion', 'dismissedDamageBonus');
      this.deps.ai.setCredenceVulnerable(enemyId, credenceDurationMs, damageBonus);
    }

    // Cascade: propagate dismiss roll to nearby enemies (non-recursive)
    if (!isCascade) {
      this._tryCascade(enemyId);
    }
  }

  private _tryCascade(sourceEnemyId: string): void {
    const cascadeRadiusTiles = this.deps.resolver.getAdditiveEffect('distortion', 'cascadeRadius');
    if (cascadeRadiusTiles <= 0) return; // Cascade not unlocked

    const cascadeChance = this.deps.resolver.getAdditiveEffect('distortion', 'cascadeChance');
    if (cascadeChance <= 0) return;

    const radiusPx = cascadeRadiusTiles * this.config.tileSizePx;
    const nearby = this.deps.ai.getNearbyEnemies(sourceEnemyId, radiusPx);

    for (const nearbyId of nearby) {
      if (this.deps.rng.roll() < cascadeChance) {
        this._onDismiss(nearbyId, true); // non-recursive: isCascade=true
      }
    }
  }
}
